import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Table,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Upload,
  Typography,
  Collapse
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PictureOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { DocsService, type DocSectionRef, type DocPageContentBlock } from '@/services/docs.service';
import { getUploadsBaseUrl, normalizeUploadImageUrl } from '@/config/api.config';
import { logger } from '@/utils/logger';

const { TextArea } = Input;
const { Title } = Typography;

type SectionWithPages = DocSectionRef & { DocPages?: Array<{ id: number; title: string; slug: string; order: number }> };

export const AdminDocsPage: React.FC = () => {
  const [sections, setSections] = useState<SectionWithPages[]>([]);
  const [loading, setLoading] = useState(true);
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [pageModalOpen, setPageModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<SectionWithPages | null>(null);
  const [editingPage, setEditingPage] = useState<{ id: number; sectionId: number; title: string; slug: string; order: number; content: string } | null>(null);
  const [form] = Form.useForm();
  const [pageForm] = Form.useForm();
  const [blocks, setBlocks] = useState<DocPageContentBlock[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const data = await DocsService.adminListSections();
      setSections(data as SectionWithPages[]);
    } catch (e) {
      logger.error('Fetch docs sections', e);
      message.error('Ошибка загрузки разделов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const handleSaveSection = async () => {
    try {
      const v = await form.validateFields();
      if (editingSection) {
        await DocsService.adminUpdateSection(editingSection.id, { title: v.title, slug: v.slug, order: v.order });
        message.success('Раздел обновлён');
      } else {
        await DocsService.adminCreateSection({ title: v.title, slug: v.slug, order: v.order });
        message.success('Раздел создан');
      }
      setSectionModalOpen(false);
      setEditingSection(null);
      form.resetFields();
      fetchSections();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Ошибка сохранения');
    }
  };

  const handleDeleteSection = async (id: number) => {
    try {
      await DocsService.adminDeleteSection(id);
      message.success('Раздел удалён');
      fetchSections();
    } catch {
      message.error('Ошибка удаления');
    }
  };

  const openPageModal = (section: SectionWithPages, page?: { id: number; sectionId: number; title: string; slug: string; order: number; content?: string }) => {
    setEditingPage(page ? { ...page, content: page.content || '{"blocks":[]}' } : null);
    if (page?.content) {
      try {
        const p = JSON.parse(page.content);
        setBlocks(Array.isArray(p.blocks) ? p.blocks : []);
      } catch {
        setBlocks([]);
      }
    } else {
      setBlocks([]);
    }
    pageForm.setFieldsValue(
      page
        ? { title: page.title, slug: page.slug, order: page.order }
        : { title: '', slug: '', order: (section.DocPages?.length || 0) }
    );
    (pageForm as any)._sectionId = section.id;
    setPageModalOpen(true);
  };

  const handleSavePage = async () => {
    try {
      const v = await pageForm.validateFields();
      const sectionId = (pageForm as any)._sectionId as number;
      const content = JSON.stringify({ blocks });
      if (editingPage) {
        await DocsService.adminUpdatePage(editingPage.id, { title: v.title, slug: v.slug, order: v.order, content });
        message.success('Страница обновлена');
      } else {
        await DocsService.adminCreatePage({ sectionId, title: v.title, slug: v.slug, order: v.order, content });
        message.success('Страница создана');
      }
      setPageModalOpen(false);
      setEditingPage(null);
      setBlocks([]);
      pageForm.resetFields();
      fetchSections();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Ошибка сохранения');
    }
  };

  const addTextBlock = () => {
    setBlocks((prev) => [...prev, { type: 'text', content: '<p>Новый абзац.</p>' }]);
  };

  const addImageBlock = () => {
    setBlocks((prev) => [...prev, { type: 'image', imageUrl: '', imagePosition: 'full', imageAlt: '' }]);
  };

  const removeBlock = (index: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
  };

  const moveBlock = (index: number, dir: 1 | -1) => {
    const next = index + dir;
    if (next < 0 || next >= blocks.length) return;
    setBlocks((prev) => {
      const arr = [...prev];
      [arr[index], arr[next]] = [arr[next], arr[index]];
      return arr;
    });
  };

  const updateBlock = (index: number, patch: Partial<DocPageContentBlock>) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  };

  const handleImageUpload = async (file: File, blockIndex: number) => {
    try {
      setUploadingImage(true);
      const { url } = await DocsService.uploadImage(file);
      updateBlock(blockIndex, { imageUrl: url });
      message.success('Изображение загружено');
    } catch {
      message.error('Ошибка загрузки изображения');
    } finally {
      setUploadingImage(false);
    }
    return false; // prevent default upload
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>
        Документация
      </Title>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingSection(null); form.setFieldsValue({ title: '', slug: '', order: sections.length }); setSectionModalOpen(true); }}>
            Добавить раздел
          </Button>
        </Space>
        {loading ? (
          <p>Загрузка...</p>
        ) : (
          <Collapse
            items={sections.map((sec) => ({
              key: String(sec.id),
              label: (
                <Space>
                  <strong>{sec.title}</strong>
                  <span style={{ color: '#999' }}>/{sec.slug}</span>
                </Space>
              ),
              extra: (
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingSection(sec); form.setFieldsValue({ title: sec.title, slug: sec.slug, order: sec.order }); setSectionModalOpen(true); }} />
                  <Popconfirm title="Удалить раздел и все страницы?" onConfirm={() => handleDeleteSection(sec.id)}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                  <Button size="small" type="primary" onClick={() => openPageModal(sec)}>
                    Добавить страницу
                  </Button>
                </Space>
              ),
              children: (
                <Table
                  size="small"
                  dataSource={sec.DocPages || []}
                  rowKey="id"
                  columns={[
                    { title: 'Название', dataIndex: 'title', key: 'title' },
                    { title: 'Slug', dataIndex: 'slug', key: 'slug', width: 180 },
                    {
                      title: 'Действия',
                      key: 'actions',
                      width: 120,
                      render: (_, record) => (
                        <Space>
                          <Button size="small" icon={<EditOutlined />} onClick={() => openPageModal(sec, { ...record, sectionId: sec.id, content: (record as any).content })} />
                          <Popconfirm title="Удалить страницу?" onConfirm={async () => { await DocsService.adminDeletePage(record.id); fetchSections(); }}>
                            <Button size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      )
                    }
                  ]}
                  pagination={false}
                />
              )
            }))}
          />
        )}
      </Card>

      <Modal
        title={editingSection ? 'Редактировать раздел' : 'Новый раздел'}
        open={sectionModalOpen}
        onOk={handleSaveSection}
        onCancel={() => { setSectionModalOpen(false); setEditingSection(null); form.resetFields(); }}
        okText="Сохранить"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Название" rules={[{ required: true }]}>
            <Input placeholder="Например: Турниры" />
          </Form.Item>
          <Form.Item name="slug" label="Slug (URL)" rules={[{ required: true }]}>
            <Input placeholder="tournaments" />
          </Form.Item>
          <Form.Item name="order" label="Порядок">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingPage ? 'Редактировать страницу' : 'Новая страница'}
        open={pageModalOpen}
        onOk={handleSavePage}
        onCancel={() => { setPageModalOpen(false); setEditingPage(null); setBlocks([]); pageForm.resetFields(); }}
        okText="Сохранить"
        width={720}
      >
        <Form form={pageForm} layout="vertical">
          <Form.Item name="title" label="Название" rules={[{ required: true }]}>
            <Input placeholder="Создание турнира" />
          </Form.Item>
          <Form.Item name="slug" label="Slug (URL)" rules={[{ required: true }]}>
            <Input placeholder="create-tournament" />
          </Form.Item>
          <Form.Item name="order" label="Порядок">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
        <Title level={5} style={{ marginTop: 16 }}>
          Содержимое (блоки)
        </Title>
        <Space style={{ marginBottom: 12 }}>
          <Button icon={<PlusOutlined />} onClick={addTextBlock}>
            Текст
          </Button>
          <Button icon={<PictureOutlined />} onClick={addImageBlock}>
            Изображение
          </Button>
        </Space>
        <div style={{ maxHeight: 400, overflow: 'auto' }}>
          {blocks.map((block, i) => (
            <Card size="small" key={i} style={{ marginBottom: 8 }}>
              <Space style={{ marginBottom: 8 }}>
                <Button size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => moveBlock(i, -1)} />
                <Button size="small" icon={<ArrowDownOutlined />} disabled={i === blocks.length - 1} onClick={() => moveBlock(i, 1)} />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeBlock(i)} />
              </Space>
              {block.type === 'text' && (
                <TextArea
                  value={block.content || ''}
                  onChange={(e) => updateBlock(i, { content: e.target.value })}
                  rows={4}
                  placeholder="HTML: <p>Текст</p>"
                />
              )}
              {block.type === 'image' && (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => { handleImageUpload(file, i); return false; }}
                    disabled={uploadingImage}
                  >
                    <Button loading={uploadingImage}>{block.imageUrl ? 'Заменить изображение' : 'Загрузить скриншот'}</Button>
                  </Upload>
                  {block.imageUrl && (
                    <img src={(normalizeUploadImageUrl(block.imageUrl.startsWith('http') ? block.imageUrl : `${getUploadsBaseUrl()}${block.imageUrl.startsWith('/') ? block.imageUrl : '/' + block.imageUrl}`) ?? block.imageUrl)} alt="" style={{ maxWidth: '100%', maxHeight: 200 }} />
                  )}
                  <Input
                    placeholder="Подпись к изображению"
                    value={block.imageAlt || ''}
                    onChange={(e) => updateBlock(i, { imageAlt: e.target.value })}
                  />
                  <Select
                    value={block.imagePosition || 'full'}
                    onChange={(val) => updateBlock(i, { imagePosition: val })}
                    style={{ width: 160 }}
                    options={[
                      { value: 'full', label: 'Во всю ширину' },
                      { value: 'left', label: 'Слева' },
                      { value: 'right', label: 'Справа' }
                    ]}
                  />
                </Space>
              )}
            </Card>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default AdminDocsPage;
